import { useState } from 'react'
import { AlertCircle, ChevronDown, ChevronUp, RotateCcw } from 'lucide-react'
import { AxiosError } from 'axios'

/** Extract HTTP status code from error */
const getStatusCode = (error: unknown): number => {
    if (error && typeof error === 'object' && 'response' in error) {
        return (error as AxiosError).response?.status ?? 500
    }
    if (error && typeof error === 'object' && 'status' in error) {
        return (error as { status: number }).status
    }
    return 500
}

/** Friendly user message based on error type */
const getUserMessage = (error: unknown): string => {
    const status = getStatusCode(error)
    if (status === 401 || status === 403)
        return 'Bạn không có quyền truy cập dữ liệu này.'
    if (status === 404) return 'Không tìm thấy dữ liệu.'
    if (status >= 500) return 'Hệ thống đang gặp sự cố. Vui lòng thử lại sau.'
    if (error instanceof Error && error.message?.includes('Network Error'))
        return 'Không thể kết nối đến máy chủ. Kiểm tra kết nối mạng.'
    return 'Đã xảy ra lỗi khi tải dữ liệu.'
}

interface QueryErrorAlertProps {
    error: unknown
    onRetry?: () => void
}

/** Inline error alert with friendly message and collapsible technical details */
const QueryErrorAlert = ({ error, onRetry }: QueryErrorAlertProps) => {
    const [showDetail, setShowDetail] = useState(false)
    const message = getUserMessage(error)
    const detail = error instanceof Error ? error.message : String(error)

    return (
        <div className="rounded-md border border-red-200 bg-red-50 p-4">
            <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-red-800">
                        {message}
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                        {onRetry && (
                            <button
                                onClick={onRetry}
                                className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800 font-medium"
                            >
                                <RotateCcw className="h-3 w-3" /> Thử lại
                            </button>
                        )}
                        <button
                            onClick={() => setShowDetail((v) => !v)}
                            className="inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-700"
                        >
                            Chi tiết{' '}
                            {showDetail ? (
                                <ChevronUp className="h-3 w-3" />
                            ) : (
                                <ChevronDown className="h-3 w-3" />
                            )}
                        </button>
                    </div>
                    {showDetail && (
                        <pre className="mt-2 text-xs text-red-600 bg-red-100 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">
                            {detail}
                        </pre>
                    )}
                </div>
            </div>
        </div>
    )
}

export default QueryErrorAlert
